import type { Color, TextStyle } from '@lite-workspace/photobook-model';
import type { MessengerLink, MessengerLinks, MessengerNetwork, PrintPartnerKey } from '@lite-workspace/dto-user';
import { pickVignetteKnobs, type VignetteKnobs } from '@lite-workspace/dto-fotoclass-book';

export type AuthType = 'otp' | 'password' | 'otp_and_password';

// `max` and `mywed` join the list; `instagram` / `tiktok` / `facebook` / `pinterest` stay only to read
// what accounts already hold — LEGACY_SOCIAL_NETWORKS below, never offered as a new choice.
// The wire needs nothing: the gateway types Contact.name as a plain string with no whitelist
// (`libs/shared/dto-user/src/lib/user.interface.ts:26`), so a new name round-trips as-is.
export type ContactName =
  | 'email'
  | 'phone'
  | 'website'
  // Not a channel: «Описание фотографа» — the free-text line of the business card.
  // It rides the same jsonb list, so it needs no column and no new save flow.
  | 'description'
  // Not a channel either: «Светлая»/«Тёмная» — which visitka the client sees,
  // and the design pick that goes with it.
  | 'visitkaTheme'
  | 'visitkaDesign'
  | 'telegram'
  | 'whatsapp'
  | 'vk'
  | 'youtube'
  | 'max'
  | 'mywed'
  | 'instagram'
  | 'tiktok'
  | 'facebook'
  | 'pinterest';

export interface Contact {
  name: ContactName;
  data: string;
  isActive: boolean;
}

export interface OnboardingProgress {
  completed: string[];
  dismissed: string[];
  /** Watched lessons. Independent of the close state — an absent bucket is an empty one, not a missing fact. */
  viewed?: string[];
  /** Hearted lessons. Independent of `viewed` too: a lesson is favorite and unwatched at once. Absent = []. */
  favorites?: string[];
}

/** Корзины одного урока: у каждой своя отметка, ставится и снимается независимо от остальных. */
export type LessonBucket = 'viewed' | 'favorites';

export const EMPTY_ONBOARDING_PROGRESS: OnboardingProgress = { completed: [], dismissed: [], viewed: [], favorites: [] };

export interface CreateProjectPreference {
  serviceId: string;
  productId: string;
  spreads: number;
  /** Последний использованный шаблон; `null` — «Без шаблона», отсутствие — выбор не назывался. */
  designId?: string | null;
  /** Vignette knobs a new project starts with; `null` — cleared, absence — never saved. */
  vignetteDefaults?: Partial<VignetteKnobs> | null;
}

export function parseCreateProjectPreference(value: unknown): CreateProjectPreference | null {
  if (!value || typeof value !== 'object') return null;
  const preference = value as Record<string, unknown>;
  const serviceId = preference['serviceId'];
  const productId = preference['productId'];
  const spreads = preference['spreads'];
  const rawDesignId = preference['designId'] ?? null;
  // Отдельная проверка и отдельный `typeof` при присвоении: внутри отрицаемой скобки общего условия
  // компилятор оставляет тип `unknown`, и сборка auth падает на TS2322.
  if (rawDesignId !== null && typeof rawDesignId !== 'string') return null;
  const designId: string | null = typeof rawDesignId === 'string' ? rawDesignId : null;
  if (
    typeof serviceId !== 'string' ||
    serviceId.length === 0 ||
    serviceId.length > 128 ||
    typeof productId !== 'string' ||
    productId.length === 0 ||
    productId.length > 128 ||
    typeof spreads !== 'number' ||
    !Number.isInteger(spreads) ||
    spreads < 1 ||
    spreads > 1000 ||
    (designId !== null && (designId.length === 0 || designId.length > 128))
  ) {
    return null;
  }

  const rawVignetteDefaults = preference['vignetteDefaults'];
  if (rawVignetteDefaults === null) return { serviceId, productId, spreads, designId, vignetteDefaults: null };
  if (!rawVignetteDefaults || typeof rawVignetteDefaults !== 'object') return { serviceId, productId, spreads, designId };
  return { serviceId, productId, spreads, designId, vignetteDefaults: pickVignetteKnobs(rawVignetteDefaults) };
}

// The close buckets are mutually exclusive on the server, so the authoritative response also EVICTS a flow it moved.
export function mergeOnboardingProgress(local: OnboardingProgress, server: OnboardingProgress): OnboardingProgress {
  const merge = (mine: string[] = [], theirs: string[] = [], evicted: string[] = []): string[] =>
    [...new Set([...mine, ...theirs])].filter(id => !evicted.includes(id));
  return {
    completed: merge(local.completed, server.completed, server.dismissed),
    dismissed: merge(local.dismissed, server.dismissed, server.completed),
    // Nothing evicts a watched lesson: closing or completing a flow does not un-watch it.
    viewed: merge(local.viewed, server.viewed),
    // Same for a heart — and a removed one sticks through the store's write journal, not through this union.
    favorites: merge(local.favorites, server.favorites),
  };
}

/** Память оформления текста: недавние и сохранённые стили фотографа. */
export interface TextStyleMemory {
  recent: TextStyle[];
  saved: TextStyle[];
}

export const EMPTY_TEXT_STYLE_MEMORY: TextStyleMemory = { recent: [], saved: [] };

function parseTextStyle(value: unknown): TextStyle | null {
  if (!value || typeof value !== 'object') return null;
  const s = value as Record<string, unknown>;
  const { fontName, fontWeight, italic, color, tracking, lineHeight, fontSize } = s;
  if (
    typeof fontName !== 'string' ||
    typeof fontWeight !== 'string' ||
    typeof italic !== 'boolean' ||
    typeof color !== 'string' ||
    typeof tracking !== 'number' ||
    typeof lineHeight !== 'number' ||
    typeof fontSize !== 'number' ||
    !(fontSize > 0)
  ) {
    return null;
  }

  // Чужие ключи не переносим: на сервер уедет ровно контракт, а не то, чем его дополнили.
  // Сохранённый цвет приходит строкой — сужаем один раз здесь, на границе разбора.
  return { fontName, fontWeight, italic, color: color as Color, tracking, lineHeight, fontSize };
}

function parseStyleList(value: unknown): TextStyle[] {
  return Array.isArray(value) ? value.map(parseTextStyle).filter((s): s is TextStyle => s !== null) : [];
}

export function parseTextStyles(value: unknown): TextStyleMemory {
  const memory = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return { recent: parseStyleList(memory['recent']), saved: parseStyleList(memory['saved']) };
}

export interface UserProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  authType: AuthType;
  hasPassword: boolean;
  contacts: Contact[];
  onboardingProgress: OnboardingProgress;
  createProjectPreference?: CreateProjectPreference | null;
  textStyles: TextStyleMemory;
  partnerKey?: PrintPartnerKey | null;
  /** `undefined` = a backend that does not know the field yet; the settings toggle stays hidden then. */
  marketingUnsubscribed?: boolean;
  /** Accounts connected through the support bot, by network; `null` = not connected. Changed only by connect/disconnect. */
  messengers?: Partial<MessengerLinks>;
  /** Networks whose social row connects through the support bot instead of taking a typed handle. */
  messengerConnect?: readonly MessengerNetwork[];
  /** Photobook shelf view; `null`/absent = never chosen, the shelf opens as cards. */
  booksView?: 'cards' | 'list' | null;
  /** `null` = not customised: the built-ins of `GET config/retouch` apply. */
  retouchPrefs?: RetouchPrefs | null;
}

export interface RetouchPresetDto {
  id: string;
  name: string;
  /** Effect id → integer 0..100. */
  strengths: Record<string, number>;
  builtin?: boolean;
}

export interface RetouchPrefs {
  presets: RetouchPresetDto[];
  compare: 'side' | 'curtain' | 'toggle';
}

export type MessengerConnection = MessengerLink;

/** The networks a photographer can add today, in the order the card shows them (spec item 7). */
export const SOCIAL_NETWORKS = ['vk', 'youtube', 'telegram', 'whatsapp', 'max', 'mywed'] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

/**
 * Dropped from the offer, kept in the data: accounts filled these before the Lab parity pass, and the
 * values stay in the database. The card renders one only while it still holds a value, read-only —
 * nobody can add a new one.
 */
export const LEGACY_SOCIAL_NETWORKS = ['instagram', 'tiktok', 'facebook', 'pinterest'] as const;
export type LegacySocialNetwork = (typeof LEGACY_SOCIAL_NETWORKS)[number];

/** Spoken name, prefix shown before the field, and what the photographer is asked to type (spec item 7). */
export const SOCIAL_NETWORK_FIELDS: Readonly<Record<SocialNetwork, { label: string; prefix: string; placeholder: string }>> = {
  vk: { label: 'VK', prefix: 'vk.com/', placeholder: 'id или ссылка' },
  youtube: { label: 'YouTube', prefix: 'youtube.com/', placeholder: 'канал или ссылка' },
  telegram: { label: 'Telegram', prefix: 't.me/', placeholder: 'username' },
  whatsapp: { label: 'WhatsApp', prefix: 'wa.me/', placeholder: 'номер телефона' },
  max: { label: 'Max', prefix: 'max.ru/', placeholder: 'профиль или ссылка' },
  mywed: { label: 'Mywed', prefix: 'mywed.com/', placeholder: 'профиль или ссылка' },
};

export type ProfilePatch = Partial<
  Pick<UserProfile, 'firstName' | 'lastName' | 'email' | 'textStyles' | 'partnerKey' | 'marketingUnsubscribed' | 'booksView'>
> & {
  contacts?: Contact[];
  /** The server merges it per key: a presets-only save keeps the stored compare mode, and back. */
  retouchPrefs?: Partial<RetouchPrefs> | null;
  /** The server merges it: an absent field keeps its stored value (a knobs-only save keeps the wizard choice). */
  createProjectPreference?: Partial<CreateProjectPreference> | null;
};
